package net.sergeych.kilogin

import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.serializer
import net.sergeych.bintools.ByteChunk
import net.sergeych.bipack.BipackEncoder
import net.sergeych.crypto2.Container
import net.sergeych.crypto2.KDF
import net.sergeych.crypto2.PBKD
import net.sergeych.crypto2.SymmetricKey

/**
 * Access Control Object (ACO) allows decrypting vital application data using either some `passwordKey`, usually
 * obtained from user password using [DerivedKeys], or a special randomly created `secret` string, which is
 * hard to remember but cryptographically string and contains some protection against typing errors,
 * see [RestoreKey] for details. Once the access control object is generated, it can be decrypted using:
 *
 * - Regenerated password key, usually [DerivedKeys.loginAccessKey]
 * - `restoreSecret`, that should only be known to a user
 *
 * It is possible to change data and/or `passwordKey` of a decrypted object. Use [packed] property on new instance
 * to save encrypted updated data.
 *
 * AccessControlObject holds also application-specific `data`.
 *
 * _Important note. While the primary constructor is public, we strongly recommend never using it directly.
 * To construct it, please use one of:_
 *
 * - [AccessControlObject.pack] to generate new
 * - [AccessControlObject.unpackWithKey] to decrypt it with a password key
 * - [AccessControlObject.unpackWithSecret] to decrypt it with a `secret`
 *
 * @param dataSerializer  serializer used to properly serialize application-specific data [payload]
 * @param packed encrypted and packed representation of the object, could be stored in public.
 * @param passwordKey the password-derived key that could be used to unpack the [packed] data
 * @param data contains necessary data to keep and operate the ACO
 */
class AccessControlObject<T>(
    private val dataSerializer: KSerializer<Data<T>>,
    val packed: UByteArray,
    val passwordKey: SymmetricKey,
    val data: Data<T>,
) {
    class InvalidFormatException(message: String) : Exception(message)

    /**
     * The application-specific data stored in the ACO. Should be @Serializable or a simple type
     */
    val payload: T by data::payload

    /**
     * The ID that can be used to index/find the stored ACO, restoreId is derived from the secret
     * with [RestoreKey] and could be obtained by application software independently. The recommended
     * use is to identify stored login record that should include a copy of most recent
     * packed AccessControlObject.
     */
    val restoreId by data::restoreId

    /**
     * Internal use only. The data ACO needs to operate, including the application's [payload]. This is
     * the content of encrypted packed ACO.
     */
    @Serializable
    class Data<T>(
        val restoreId: ByteChunk,
        private val _restoreKey: SymmetricKey,
        val restoreParams: PBKD.Params,
        val payload: T,
    ) {
        val restoreKey: SymmetricKey by lazy {
            SymmetricKey(_restoreKey.keyBytes, restoreParams)
        }
    }

    /**
     * Update payload and return new instance of te ACO. The `secret` and `passwordKey` are not changed.
     * Use [packed] property on the result to save updated copy.
     */
    inline fun <reified R> updatePayload(newPayload: R): AccessControlObject<R> {
        val data = Data(restoreId, data.restoreKey, data.restoreParams, newPayload)
        return AccessControlObject(
            serializer<Data<R>>(),
            Container.encrypt(data, passwordKey, data.restoreKey),
            passwordKey, data
        )
    }

    /**
     * Update the password key and return new instance. It could be decrypted with the same `secret` and
     * [newPasswordKey] only. The [payload] is not changed.
     */
    fun updatePasswordKey(newPasswordKey: SymmetricKey): AccessControlObject<T> {
        val data = Data(restoreId, data.restoreKey, data.restoreParams, payload)
        return AccessControlObject(
            dataSerializer,
            Container.create(
                BipackEncoder.encode(dataSerializer, data).toUByteArray()
            ) { key(newPasswordKey, data.restoreKey) }
                .encoded,
            newPasswordKey, data
        )

    }

//    class WrongKeyException : Exception("can't decrypt AccessControlObject")

    companion object {

        /**
         * Create and pack a brand-new access control object with new random `secret`. It returns [RestoreKey]
         * instance which contains not only `secret` but also `restoreId` which are often needed to save (and identify)
         * an encrypted object in the cloud, and also the key used to encrypt it. _Never save the key to disk or
         * transmit it over the network_. When needed, derive it from s `secret` again, see [RestoreKey.parse].
         * @param passwordKey the second key that is normally used to decrypt ACO
         * @param payload application-specific data to keep inside the ACO
         * @return pair where first is a [RestoreKey] instance containing new `secret` and so on, and packed encrypted
         *          ACO that could be restored using `secret` or a [passwordKey]
         */
        inline fun <reified T> pack(
            passwordKey: SymmetricKey,
            payload: T,
            complexity: KDF.Complexity = KDF.Complexity.Sensitive
        ): Pair<RestoreKey, UByteArray> {
            val restoreKey = RestoreKey.generate(complexity)
            check(restoreKey.key.id.kdp != null)
            return restoreKey to Container.encrypt(
                Data(restoreKey.restoreId, restoreKey.key, restoreKey.key.id.kdp!!, payload),
                passwordKey, restoreKey.key
            )
        }

        /**
         * Unpack and decrypt ACO with a password key or secret-based key (this once can be obtained from `secret`
         * with [RestoreKey.parse].
         * @return decrypted ACO or null if the key is wrong.
         */
        inline fun <reified T> unpackWithKey(packed: UByteArray, key: SymmetricKey): AccessControlObject<T>? {
            // We must not loose restoreKer KDP:
            return Container.decrypt<Data<T>>(packed.toUByteArray(), key)?.let {
                AccessControlObject(serializer<Data<T>>(), packed, key, it)
            }
        }

        fun <T> unpackWithKey(
            packed: UByteArray,
            passwordKey: SymmetricKey,
            serializer: KSerializer<Data<T>>,
        ): AccessControlObject<T>? =
            Container.decryptAsUBytes(packed.toUByteArray(), passwordKey)?.let {
                AccessControlObject(
                    serializer<Data<T>>(), packed, passwordKey,
                    biDecode(serializer, packed)
                )
            }

        /**
         * Unpack and decrypt ACO with a [secret]. If you want to check [secret] integrity, use
         * [RestoreKey.checkSecretIntegrity].
         * @return decrypted ACO or null if the secret is wrong
         */
        inline fun <reified T> unpackWithSecret(packed: UByteArray, secret: String): AccessControlObject<T>? {
            try {
                val c = Container.decode(packed)
                // find the restore key
                if( c.keyIds.size < 2 ) throw InvalidFormatException("too few keys in container")
                val kid = c.keyIds[1]
                val kdp = kid.kdp ?: throw InvalidFormatException("wrong keyId for restoring key in container")
                return unpackWithKey(packed, kdp.deriveKey(RestoreKey.p(secret)))
            } catch (_: RestoreKey.InvalidSecretException) {
                return null
            }
        }
    }
}